import {useCallback, useContext} from 'react';

import {
    NO_ACCOUNT_NAME,
    NO_ACCOUNT_TEMPLATE_USER,
    NO_COUNTRY,
    VALIDATION_ERRORS,
} from 'src/components/account-information/constants';
import {ValidationContext} from 'src/contexts/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {NO_PRIMARY_ATM, NO_PRIMARY_ATM_MESSAGE} from 'src/modules/create-account/constants';
import {AccountPreset} from 'src/modules/profile/account/tabs/settings/sections/entitlement-settings/useAccountPreset';
import {createConferenceAccount} from 'src/openapi/admin/apis/ConferenceApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    CONFERENCE_NAME_CONTROL,
    CONFERENCE_NAME_REQUIRED_ERROR,
    CONFERENCE_NAME_REQUIRED_MESSAGE,
    END_DATE_CONTROL,
    END_DATE_REQUIRED_ERROR,
    END_DATE_REQUIRED_MESSAGE,
    LICENSE_END_DATE_BEFORE_START_DATE_ERROR,
    LICENSE_END_DATE_BEFORE_START_DATE_MESSAGE,
    LICENSE_END_DATE_CONTROL,
    LICENSE_END_DATE_REQUIRED_ERROR,
    LICENSE_END_DATE_REQUIRED_MESSAGE,

    LICENSE_START_DATE_CONTROL,
    LICENSE_START_DATE_REQUIRED_ERROR,
    LICENSE_START_DATE_REQUIRED_MESSAGE,

    START_DATE_CONTROL,
    START_DATE_REQUIRED_ERROR,
    START_DATE_REQUIRED_MESSAGE,
} from './constants';

import type {AccountTeamMember} from 'src/modules/create-account/typedef';
import type {SuccessPayload} from 'src/typedef';

export type CreateConferenceData = {
    accountName: string,
    preset: AccountPreset,
    conferenceName: string,
    countryCode: string,
    templateId: number,
    confStartDate: string,
    confEndDate: string,
    licenseStartDate?: string,
    licenseEndDate?: string,
    managerRelations: AccountTeamMember[],
};
const emptyPreset = {presetId: '', presetName: '', presetDescription: ''};

const DEFAULT_VALUE: CreateConferenceData = {
    accountName: '',
    preset: emptyPreset,
    conferenceName: '',
    countryCode: 'US',
    templateId: 0,
    confStartDate: '',
    confEndDate: '',
    managerRelations: [{
        primary: true,
        type: 'AM',
        receiveAccountAlerts: true,
    }],
};

export const useCreateConference = () => {
    const apiDispatch = useAppDispatch();
    const {addError, validationErrors, removeError} = useContext(ValidationContext);
    const {getLocalDate} = useDateTime();

    const getDispatchAction = useCallback(() => {
        const payload: SuccessPayload<CreateConferenceData> = {
            data: DEFAULT_VALUE,
            success: true,
            error: false,
        };
        return Promise.resolve({type: '', payload});
    }, []);

    const saveDispatchAction = useCallback(async (
        createConferenceRequest: CreateConferenceData,
        signal: AbortSignal,
    ) => {
        const response = await apiDispatch(createConferenceAccount({
            createConferenceAccountRequest: {
                ...createConferenceRequest,
                presetId: createConferenceRequest.preset.presetId,
                managerRelations: {relations: createConferenceRequest.managerRelations
                    .filter(teamMember => teamMember.user?.id)
                    .map(teamMember => ({
                        accountManagerId: teamMember.user?.id,
                        primary: teamMember.primary,
                        receiveAlert: teamMember.receiveAccountAlerts,
                        typeCode: teamMember.type,
                    })),
                },
            },
        }, {signal}));

        if (response.payload.success) {
            return Promise.resolve({type: '', payload: {
                success: true,
                error: false,
                data: response.payload.data.accountId,
            } as SuccessPayload<number>});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [apiDispatch]);

    const editValidation = useCallback((data: CreateConferenceData) => {
        if (data.confStartDate || (!data.conferenceName && !data.confEndDate)) {
            removeError(START_DATE_CONTROL, [START_DATE_REQUIRED_ERROR]);
        }

        if (data.confEndDate || (!data.conferenceName && !data.confStartDate)) {
            removeError(END_DATE_CONTROL, [END_DATE_REQUIRED_ERROR]);
        }

        if (data.accountName) {
            removeError('account-name', [NO_ACCOUNT_NAME]);
        }

        if (data.conferenceName || (!data.confEndDate && !data.confStartDate)) {
            removeError(CONFERENCE_NAME_CONTROL, [CONFERENCE_NAME_REQUIRED_ERROR]);
        }

        if (data.templateId) {
            removeError('account-template-user', [NO_ACCOUNT_TEMPLATE_USER]);
        }

        if (data.countryCode) {
            removeError('account-country-select', [NO_COUNTRY]);
        }

        if (!data.licenseStartDate || data.licenseEndDate) {
            removeError(LICENSE_END_DATE_CONTROL, [LICENSE_END_DATE_REQUIRED_ERROR]);
        }

        if (!data.licenseEndDate || data.licenseStartDate) {
            removeError(LICENSE_START_DATE_CONTROL, [LICENSE_START_DATE_REQUIRED_ERROR]);
        }

        if (getLocalDate(data.confEndDate) >= getLocalDate(data.confStartDate)) {
            removeError(END_DATE_CONTROL, [LICENSE_END_DATE_BEFORE_START_DATE_ERROR]);
        }

        if (getLocalDate(data.licenseEndDate) >= getLocalDate(data.licenseStartDate)) {
            removeError(LICENSE_END_DATE_CONTROL, [LICENSE_END_DATE_BEFORE_START_DATE_ERROR]);
        }

        data.managerRelations.forEach((teamMember, i) => {
            if (teamMember.primary && teamMember.user?.id) {
                removeError(`account-team-member-select-${i}`);
            }
        });
    }, [getLocalDate, removeError]);

    const saveValidation = useCallback((data: CreateConferenceData) => {
        let valid = true;

        if (validationErrors.length) {
            valid = false;
        }

        if (!data.accountName) {
            addError({
                code: NO_ACCOUNT_NAME,
                message: VALIDATION_ERRORS.NO_ACCOUNT_NAME,
                controlId: 'account-name',
            });
            valid = false;
        }

        if ((data.confStartDate || data.confEndDate) && !data.conferenceName) {
            addError({
                controlId: CONFERENCE_NAME_CONTROL,
                code: CONFERENCE_NAME_REQUIRED_ERROR,
                message: CONFERENCE_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.templateId) {
            addError({
                code: NO_ACCOUNT_TEMPLATE_USER,
                message: VALIDATION_ERRORS.NO_ACCOUNT_TEMPLATE_USER,
                controlId: 'account-template-user',
            });
            valid = false;
        }

        if (!data.countryCode) {
            addError({
                code: NO_COUNTRY,
                message: VALIDATION_ERRORS.NO_COUNTRY,
                controlId: 'account-country-select',
            });
            valid = false;
        }

        if ((data.conferenceName || data.confEndDate) && !data.confStartDate) {
            addError({
                controlId: START_DATE_CONTROL,
                code: START_DATE_REQUIRED_ERROR,
                message: START_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if ((data.conferenceName || data.confStartDate) && !data.confEndDate) {
            addError({
                controlId: END_DATE_CONTROL,
                code: END_DATE_REQUIRED_ERROR,
                message: END_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (data.licenseStartDate && !data.licenseEndDate) {
            addError({
                controlId: LICENSE_END_DATE_CONTROL,
                code: LICENSE_END_DATE_REQUIRED_ERROR,
                message: LICENSE_END_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (data.licenseEndDate && !data.licenseStartDate) {
            addError({
                controlId: LICENSE_START_DATE_CONTROL,
                code: LICENSE_START_DATE_REQUIRED_ERROR,
                message: LICENSE_START_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (getLocalDate(data.confEndDate) < getLocalDate(data.confStartDate)) {
            addError({
                controlId: END_DATE_CONTROL,
                code: LICENSE_END_DATE_BEFORE_START_DATE_ERROR,
                message: LICENSE_END_DATE_BEFORE_START_DATE_MESSAGE,
            });
            valid = false;
        }

        if (getLocalDate(data.licenseEndDate) < getLocalDate(data.licenseStartDate)) {
            addError({
                controlId: LICENSE_END_DATE_CONTROL,
                code: LICENSE_END_DATE_BEFORE_START_DATE_ERROR,
                message: LICENSE_END_DATE_BEFORE_START_DATE_MESSAGE,
            });
            valid = false;
        }

        data.managerRelations.forEach((teamMember, i) => {
            if (teamMember.primary && !teamMember.user?.id) {
                addError({
                    code: NO_PRIMARY_ATM,
                    message: NO_PRIMARY_ATM_MESSAGE,
                    controlId: `account-team-member-select-${i}`,
                });
                valid = false;
            }
        });

        return valid;
    }, [validationErrors, getLocalDate, addError]);

    return useCommonDataHook({
        initValue: DEFAULT_VALUE,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
